import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { usePricing } from '../../contexts/PricingContext';
import { Cancel } from '@mui/icons-material';
import { formatPrice } from '../../utils/subscriptionCalculator';
import { formatDisplayPrice } from '../../utils/currencyUtils';
import { MonobankIcon, ABankIcon } from '../icons/BankIcons';

/**
 * SubscriptionPlanDisplay component - unified subscription plan display
 * @param {Object} props
 * @param {Object} props.plan - Subscription plan data
 * @param {boolean} props.isAvailable - Whether the plan is available (default: true)
 * @param {boolean} props.showSavings - Show savings information (default: true)
 * @param {string} props.variant - Display variant: 'default', 'compact', 'minimal' (default: 'default')
 * @param {boolean} props.hasMultipleOptions - Whether there are multiple subscription options (affects label)
 * @param {Object} props.sx - Additional styles
 */
const SubscriptionPlanDisplay = ({ 
  plan,
  isAvailable = true,
  showSavings = true,
  variant = 'default',
  hasMultipleOptions = true,
  region = null,  // Region for currency conversion
  customDiscount360 = 20,  // Custom 360-day discount percentage
  customDiscount180 = 0,  // Custom 180-day discount percentage
  sx = {}
}) => {
  const { t } = useTranslation();
  
  // Get user preferences and pricing data for currency conversion
  // Always call hooks unconditionally, then handle undefined values
  const { displayCurrency = 'USD' } = useUserPreferences() || {};
  const { currencyRates = {} } = usePricing() || {};
  
  // Use region parameter instead of context region
  
  if (!plan) return null;
  
  // Get plan label - use translations with custom discount percentages
  const getPlanLabel = () => {
    if (plan.id === '360-days') {
      // Hide bonus text if discount is 0%
      if (customDiscount360 === 0) {
        return t('subscription.360DaysNoBonus', '360 days');
      }
      return hasMultipleOptions
        ? t('subscription.360DaysCustom', `360 days (bonus {{discount}}%)`, { discount: customDiscount360 })
        : t('subscription.360DaysNoBonus', '360 days');
    }
    if (plan.id === '180-days') {
      // Hide bonus text if discount is 0%
      if (customDiscount180 === 0) {
        return t('subscription.180Days', '180 days');
      }
      return t('subscription.180DaysCustom', `180 days (bonus {{discount}}%)`, { discount: customDiscount180 });
    }

    // Static translations for other plans
    const labels = {
      '90-days': t('subscription.90Days', '90 days'),
      '12-month-contract': t('subscription.12MonthContract', '12-monthly contract'),
      '360-days-split': t('subscription.360DaysSplit', '360 days (installment)')
    };
    return labels[plan.id] || plan.id;
  };
  
  // Minimal variant - for history page
  if (variant === 'minimal') {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        bgcolor: plan.isDefault || plan.isRecommended ? 'rgba(0, 200, 83, 0.05)' : 'transparent',
        border: plan.isDefault || plan.isRecommended ? '1px solid rgba(0, 200, 83, 0.2)' : 'none',
        ...sx
      }}>
        <Typography variant="caption" sx={{
          fontSize: '0.7rem',
          fontWeight: plan.isDefault || plan.isRecommended ? 600 : 400
        }}>
          {getPlanLabel()}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {plan.id === '360-days-split' && (
            <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
              <MonobankIcon size={14} />
              <ABankIcon size={14} />
            </Box>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: '#00C853'
            }}>
              {formatDisplayPrice(plan.paymentAmount || plan.payment_amount || 0, displayCurrency, currencyRates, region)} {t('subscription.toPay', 'to pay')}
            </Typography>
            <Typography variant="caption" sx={{
              fontSize: '0.65rem',
              color: 'text.secondary',
              display: 'block'
            }}>
              {formatDisplayPrice(plan.monthlyPrice || plan.monthly_price || 0, displayCurrency, currencyRates, region)}/month
            </Typography>
            {showSavings && (plan.savings || plan.discountAmount || plan.discount_amount) > 0 && (
              <Typography variant="caption" sx={{
                fontSize: '0.65rem',
                color: '#00C853',
                fontWeight: 500,
                display: 'block'
              }}>
                Saves {formatDisplayPrice(plan.savings || plan.discountAmount || plan.discount_amount || 0, displayCurrency, currencyRates, region)}/year
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
  
  // Compact variant - for comparison page
  if (variant === 'compact') {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        py: 1.5,
        minHeight: '70px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        opacity: isAvailable ? 1 : 0.5,
        ...sx
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" sx={{
            fontWeight: 500,
            color: isAvailable ? 'text.primary' : 'text.secondary'
          }}>
            {getPlanLabel()}
          </Typography>
          {isAvailable && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline' }}>
                  {formatDisplayPrice(plan.monthlyPrice || plan.monthly_price || 0, displayCurrency, currencyRates, region)}{t('offers.perMonth', '/mo')}
                </Typography>
                {showSavings && (plan.savings || plan.discountAmount || plan.discount_amount) > 0 && (
                  <Typography variant="caption" sx={{ color: '#00C853', display: 'inline', ml: 0.5 }}>
                    ({t('offers.savePerYear', 'Save')} {formatDisplayPrice(plan.savings || plan.discountAmount || plan.discount_amount || 0, displayCurrency, currencyRates, region)})
                  </Typography>
                )}
              </Box>
              {plan.id === '360-days-split' && (
                <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                  <MonobankIcon size={14} />
                  <ABankIcon size={14} />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 1 }}>
          {isAvailable ? (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDisplayPrice(plan.paymentAmount || plan.payment_amount || 0, displayCurrency, currencyRates, region)} {t('offers.toPay', 'to pay')}
              </Typography>
              {/* Show VAT for Ukraine */}
              {region === 'ua' && (
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: '0.75rem'
                }}>
                  {t('subscription.vatIncluded', 'Including VAT 20%: {{amount}}', {
                    amount: formatDisplayPrice((plan.paymentAmount || plan.payment_amount || 0) * 1.2, displayCurrency, currencyRates, region)
                  })}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                fontStyle: 'italic'
              }}>
                {t('offers.notIncluded', 'Not included')}
              </Typography>
              <Cancel sx={{ color: '#f44336', fontSize: 18 }} />
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  
  // Default variant - for offer page and price summary
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        mb: 1.5, 
        bgcolor: plan.isRecommended ? 'rgba(0, 200, 83, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: plan.isRecommended ? '1px solid rgba(0, 200, 83, 0.2)' : '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 1,
        opacity: isAvailable ? 1 : 0.5,
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ 
            fontWeight: 500,
            color: isAvailable ? 'text.primary' : 'text.secondary'
          }}>
            {getPlanLabel()}
          </Typography>
          {!isAvailable && (
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              bgcolor: '#f44336', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>
                ✕
              </Typography>
            </Box>
          )}
        </Box>
        {isAvailable && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatDisplayPrice(plan.paymentAmount || plan.payment_amount || 0, displayCurrency, currencyRates, region)} {t('subscription.toPay', 'to pay')}
            </Typography>
            {showSavings && plan.savings > 0 && (
              <Typography variant="caption" sx={{ color: '#00C853', fontWeight: 500 }}>
                {t('subscription.savings', 'Savings')} {formatDisplayPrice(plan.savings, displayCurrency, currencyRates, region)}
              </Typography>
            )}
            {showSavings && plan.overpayment > 0 && (
              <Typography variant="caption" sx={{ color: '#f44336' }}>
                +{formatDisplayPrice(plan.overpayment, displayCurrency, currencyRates, region)} {t('subscription.perYear', '/year')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      {isAvailable && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#00C853', fontWeight: 600 }}>
            {formatDisplayPrice(plan.monthlyPrice || plan.monthly_price || 0, displayCurrency, currencyRates, region)}{t('subscription.perMonth', '/mo')}
          </Typography>
          {plan.annualPrice && (
            <Typography variant="body2" sx={{ color: '#00C853', fontWeight: 600 }}>
              {formatDisplayPrice(plan.annualPrice || plan.annual_price || 0, displayCurrency, currencyRates, region)} {t('subscription.perYear', 'per year')}
            </Typography>
          )}
        </Box>
      )}
      
      {!isAvailable && (
        <Box sx={{ mt: 'auto', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic'
          }}>
            {t('offers.notAvailable', 'Not available')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SubscriptionPlanDisplay;